import { KeyboardEvent, useRef, useEffect, useState } from 'react'
import styles from './DropDown.module.scss'
import cx from 'classnames'
import { DropDownOption } from '~types'

interface DropDownMenuProps {
  menuItems: DropDownOption[]
  selectedValue: string
  focusedIndex: number
  menuId: string
  dropdownId: string
  onOptionClick: (value: string) => void
  onOptionKeyDown: (event: KeyboardEvent<HTMLDivElement>, value: string) => void
  setFocusedIndex: (index: number) => void
  hasButtonStyles: boolean
  parentRef?: React.RefObject<HTMLElement>
}

const DropDownMenu = ({
  menuItems,
  selectedValue,
  focusedIndex,
  menuId,
  dropdownId,
  onOptionClick,
  onOptionKeyDown,
  setFocusedIndex,
  hasButtonStyles,
  parentRef
}: DropDownMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null)
  const [menuPosition, setMenuPosition] = useState<'left' | 'right'>('left')

  useEffect(() => {
    const calculatePosition = () => {
      if (!menuRef.current || !parentRef?.current) return

      const menuRect = menuRef.current.getBoundingClientRect()
      const parentRect = parentRef.current.getBoundingClientRect()

      // Check if menu would overflow on the right and adding an extra padding of 24px
      const wouldOverflowRight = parentRect.left + menuRect.width + 24 > window.innerWidth

      // Calculate available space
      const spaceOnRight = window.innerWidth - parentRect.left
      const spaceOnLeft = parentRect.right

      if (wouldOverflowRight && spaceOnLeft > spaceOnRight) {
        setMenuPosition('right')
      } else {
        setMenuPosition('left')
      }
    }

    calculatePosition()

    window.addEventListener('resize', calculatePosition)
    return () => window.removeEventListener('resize', calculatePosition)
  }, [hasButtonStyles, parentRef, menuItems])

  return (
    <div
      ref={menuRef}
      id={menuId}
      className={cx(
        styles.dropdownMenu,
        hasButtonStyles && styles.hasButtonStyles,
        menuPosition === 'right' && styles.positionRight
      )}
      role='listbox'
      aria-labelledby={`${dropdownId}-label`}
      aria-activedescendant={focusedIndex >= 0 ? `${menuId}-option-${focusedIndex}` : undefined}
      tabIndex={-1}
    >
      {menuItems.map((option, index) => {
        const value = option.value || ''
        const label = option.label || option.value || ''
        const isSelected = selectedValue === value
        const isFocused = focusedIndex === index
        const optionId = `${menuId}-option-${index}`

        return (
          <MenuItem
            key={value || index}
            id={optionId}
            value={value}
            label={label}
            isSelected={isSelected}
            isFocused={isFocused}
            onClick={onOptionClick}
            onKeyDown={onOptionKeyDown}
            onMouseEnter={() => setFocusedIndex(index)}
          />
        )
      })}
    </div>
  )
}

interface MenuItemProps {
  id: string
  value: string
  label: string
  isSelected: boolean
  isFocused: boolean
  onClick: (value: string) => void
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>, value: string) => void
  onMouseEnter: () => void
}

const MenuItem = ({ id, value, label, isSelected, isFocused, onClick, onKeyDown, onMouseEnter }: MenuItemProps) => {
  return (
    <div
      id={id}
      className={cx(styles.dropdownOption, isSelected && styles.selectedOption, isFocused && styles.focusedOption)}
      role='option'
      aria-selected={isSelected}
      tabIndex={isFocused ? 0 : -1}
      onClick={() => onClick(value)}
      onKeyDown={e => onKeyDown(e, value)}
      onMouseEnter={onMouseEnter}
      aria-label={label}
      data-value={value}
    >
      {label}
    </div>
  )
}

export default DropDownMenu
